import React from 'react'

import { Html } from '@application/Interfaces/Atoms/Html'
import { OrganizationLogo } from '@application/Interfaces/Atoms/OrganizationLogo'
import { LogoCarousel } from '@application/Interfaces/Features/LogoCarousel'
import { useGetFooter } from '@application/Interfaces/Hooks/useGetFooter'
import { useOrganization } from '@application/Interfaces/Hooks/useOrganization/useOrganization'
import { FacebookIcon, LinkedInIcon, TwitterIcon } from '@application/Interfaces/Icons'
import { GetInTouchType, LinkType } from '@application/Types/FooterTypes'
import type { Locale } from '@application/Types/Locale'
import { Grid } from '@icontainers/design-system/dist/atoms/Grid'
import { FooterThemeModeType, NavigationThemeType, SliderItem } from '@src/Application/Types/ComponentTypes'
import { Nullable, Opt } from '@src/Application/Types/Opt'

import {
    CopyrightWrapper,
    CountryLabel,
    Description,
    ItemWrapper,
    Link,
    LogoWrapper,
    MinimalCopyrightWrapper,
    MinimalFooterWrapper,
    MinimalSocialLinksWrapper,
    PhoneNumber,
    Separator,
    SocialLinkWrapper,
    Title,
    Wrapper,
    WrapperContent,
} from './Footer.styled'
import { FooterLinks } from './FooterLinks'

export const Footer = ({
    language = 'en',
    mode = 'Light',
    type,
    logoSlideItems,
}: {
    language?: Locale
    mode?: FooterThemeModeType
    type?: NavigationThemeType
    logoSlideItems?: Opt<SliderItem[]>
}): Nullable<JSX.Element> => {
    const footer = useGetFooter(language)

    const organization = useOrganization()

    if (!footer) return null

    const socialFragment = (
        <>
            {footer.facebookLink && (
                <a href={footer.facebookLink} target="_blank" rel="noreferrer">
                    <FacebookIcon colorCode={mode === 'Dark' ? 'white' : 'g500'} />
                </a>
            )}
            {footer.linkedInLink && (
                <a href={footer.linkedInLink} target="_blank" rel="noreferrer">
                    <LinkedInIcon colorCode={mode === 'Dark' ? 'white' : 'g500'} />
                </a>
            )}
            {footer.twitterLink && (
                <a href={footer.twitterLink} target="_blank" rel="noreferrer">
                    <TwitterIcon colorCode={mode === 'Dark' ? 'white' : 'g500'} />
                </a>
            )}
        </>
    )

    const logoCarouselComponent = Boolean(footer.showLogoCarousel) && logoSlideItems && (
        <LogoCarousel logos={logoSlideItems} />
    )

    const logoFragment = (
        <LogoWrapper>
            <OrganizationLogo
                mode={mode}
                width={organization.logoDimensions.footer.width}
                height={organization.logoDimensions.footer.height}
            />
        </LogoWrapper>
    )

    if (type === 'Minimal') {
        return (
            <>
                {logoCarouselComponent}
                <MinimalFooterWrapper $mode={mode}>
                    <Grid container>
                        <Grid item xs={12}>
                            {logoFragment}
                        </Grid>
                        <Grid item xs={12} component={MinimalSocialLinksWrapper} spacingTop={6} spacingBottom={6}>
                            {socialFragment}
                        </Grid>
                        <Grid item xs={12} component={MinimalCopyrightWrapper}>
                            {footer.copyrightText}
                        </Grid>
                    </Grid>
                </MinimalFooterWrapper>
            </>
        )
    }

    return (
        <>
            {logoCarouselComponent}
            {footer.seoLinksSection && <FooterLinks links={footer.seoLinksSection} />}
            <Wrapper $mode={mode}>
                <WrapperContent>
                    <Grid container>
                        <Grid item xs={12} md={4} spacing={3}>
                            {logoFragment}
                            <Description>
                                <Html content={footer.description.data.childMarkdownRemark.html} />
                            </Description>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container>
                                <Grid item xs={12} md={7} spacing={3}>
                                    <Grid container>
                                        <Grid item xs={6}>
                                            <Title>{footer.resourcesHeader}</Title>
                                            {footer.resourcesLinks.map((link: LinkType) => (
                                                <ItemWrapper key={link.link}>
                                                    <Link to={link.link}>{link.label}</Link>
                                                </ItemWrapper>
                                            ))}
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Title>{footer.aboutUsHeader}</Title>
                                            {footer.aboutUsLinks.map((link: LinkType) => (
                                                <ItemWrapper key={link.link}>
                                                    <Link to={link.link}>{link.label}</Link>
                                                </ItemWrapper>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={5} spacing={3}>
                                    <Title>{footer.getInTouchHeader}</Title>
                                    {footer.getInTouchItems.map((data: GetInTouchType) => (
                                        <ItemWrapper key={data.countryName}>
                                            <CountryLabel>{data.countryName}:</CountryLabel>
                                            <PhoneNumber>{data.phoneNumber}</PhoneNumber>
                                        </ItemWrapper>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </WrapperContent>
                <Separator />
                <WrapperContent>
                    <Grid container component={CopyrightWrapper}>
                        <Grid item xs={12} md={6}>
                            {footer.copyrightText}
                        </Grid>
                        <Grid item xs={12} md={6} component={SocialLinkWrapper}>
                            {socialFragment}
                        </Grid>
                    </Grid>
                </WrapperContent>
            </Wrapper>
        </>
    )
}
